import React from 'react';

import { humanizeKey } from '../../util/listingHelpers';
import { createSlug } from '../../util/urlHelpers';
import { NamedLink } from '../../components';

import ExpandableSection from './ExpandableSection';
import css from './ListingPage.module.css';

/**
 * Variants table for a product group. Each row is one item (SKU) in the group; the columns
 * are the DYNAMIC specifications that change between items (the group's axis keys). Stable
 * specs common to all variants are shown in SectionSpecsMaybe instead. The current listing
 * is highlighted; every other row links to that variant's product page.
 */
const SectionVariantsMaybe = props => {
  const { productGroup, variants, currentId, intl } = props;

  const axisKeys = productGroup && productGroup.axes ? Object.keys(productGroup.axes) : [];
  if (!Array.isArray(variants) || variants.length < 2) return null;

  // Sort rows by the axis columns in order: numeric values ascending (min → max),
  // non-numeric alphabetically. So sizes read 16, 20, 25… instead of extraction order.
  const toNum = s => {
    const n = parseFloat(String(s).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };
  const sortedVariants = [...variants].sort((a, b) => {
    for (const k of axisKeys) {
      const av = a.axisValues && a.axisValues[k];
      const bv = b.axisValues && b.axisValues[k];
      const an = toNum(av);
      const bn = toNum(bv);
      const cmp =
        an !== null && bn !== null ? an - bn : String(av || '').localeCompare(String(bv || ''));
      if (cmp !== 0) return cmp;
    }
    return 0;
  });

  return (
    <ExpandableSection
      className={css.sectionMargins}
      title={intl.formatMessage(
        { id: 'ListingPage.variantsTitle' },
        { count: variants.length }
      )}
    >
      <div className={css.variantsTableWrapper}>
        <table className={css.variantsTable}>
          <thead>
            <tr>
              <th>{intl.formatMessage({ id: 'ListingPage.variantColumn' })}</th>
              {axisKeys.map(k => (
                <th key={k}>{humanizeKey(k)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedVariants.map(v => {
              const isCurrent = v.materialId === currentId;
              // Show the product NAME in the first column (understandable at a glance); the
              // per-spec columns carry how each variant differs. Fall back to the axis label
              // only when a variant has no name.
              const label = v.name || v.variantLabel || '—';
              const rowCells = (
                <>
                  <td className={css.variantName}>
                    {isCurrent ? (
                      <strong>{label}</strong>
                    ) : (
                      <NamedLink
                        name="ListingPage"
                        params={{ id: v.materialId, slug: createSlug(v.name || label) }}
                      >
                        {label}
                      </NamedLink>
                    )}
                  </td>
                  {axisKeys.map(k => (
                    <td key={k}>{v.axisValues && v.axisValues[k] != null ? v.axisValues[k] : '—'}</td>
                  ))}
                </>
              );
              return (
                <tr key={v.materialId} className={isCurrent ? css.variantCurrentRow : undefined}>
                  {rowCells}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ExpandableSection>
  );
};

export default SectionVariantsMaybe;
